import React, {useContext, useEffect, useRef, useState} from 'react';
import styles from './styles/SchoolFeedWidget.module.scss';
import Button from "../utilities/Button";
import {SchoolFeedLogo} from "./SchoolFeedLogo";
import {StoreContext} from "../../stores/StoreLoader";
import {Scrollbar} from "react-scrollbars-custom";
import FontAwesome from "../utilities/FontAwesome";
import SchoolFeedList from "./SchoolFeedList";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import {observer} from "mobx-react";
import {ErrorBoundary} from "../utilities/ErrorBoundary";
import {OrganizationTypesWithGrids} from "../../types/OrganizationTypeValues";
import SafeRouter from "../utilities/SafeRouter";
import {SchoolFeedStore} from "../../stores/SchoolFeedStore";

const schoolFeedStore = new SchoolFeedStore();
export const SchoolFeedStoreContext = React.createContext(schoolFeedStore);

const SchoolFeedWidget: React.FC<{
    organization_id: string,
}> = observer(props => {
    const schoolFeedStore = useContext(SchoolFeedStoreContext)
    const {organizationStore, userStore, interfaceStore} = useContext(StoreContext);
    const isOpen = interfaceStore.schoolFeedWidgetOpen;
    const [hasFetchedInitialContent, setHasFetchedInitialContent] = useState(false);
    const [contentHeight, setContentHeight] = useState(600);

    const ref = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const handleClick = (e) => {
            const sp = document.getElementById("sb-admin-side-panel");
            const sf = document.querySelector('[aria-label="Open SchoolFeed"]')
            if (ref.current && !ref.current.contains(e.target) && !sp?.contains(e.target) && interfaceStore.schoolFeedWidgetOpen && !sf?.contains(e.target)) {
                interfaceStore.toggleSchoolFeedWidgetOpen();
            }
        };

        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    async function fetchMore() {
        const q = {
            organization__path__ancestor_or_eq: organizationStore.currentOrganization.path,
            organization__type__in: OrganizationTypesWithGrids,
        }
        if (!userStore.editor) {
            q["published"] = true;
        }
        await schoolFeedStore.fetchMore(q);
    }

    useAsyncEffect(async () => {
        if (isOpen && !hasFetchedInitialContent) {
            await fetchMore();
            setHasFetchedInitialContent(true);
        }
    }, [isOpen]);

    useEffect(() => {
        const header = document.getElementById("primaryNav");
        if (header) {
            const windowHeight = window.innerHeight;
            const headerHeight = header.clientHeight;
            setContentHeight(windowHeight - headerHeight - 80);
        }
    }, []);

    useEffect(() => {
        function handleKeyDown(e) {
            if (e.key === "Escape" && interfaceStore.schoolFeedWidgetOpen) {
                interfaceStore.toggleSchoolFeedWidgetOpen();
                buttonRef.current?.focus();
            }
        }
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [])

    return <ErrorBoundary>
        <SchoolFeedStoreContext.Provider value={schoolFeedStore}>
            <div className={styles.schoolfeed} style={{
                bottom: -contentHeight,
            }}>
                <div className={styles.content} ref={ref} style={{
                    transform: `translateY(${isOpen ? -contentHeight : 0}px)`,
                }}>
                    <button id={"schoolfeed"} ref={buttonRef} className={styles.header} onClick={() => interfaceStore.toggleSchoolFeedWidgetOpen()} aria-label={"Toggle SchoolFeed Widget"}>
                        <SchoolFeedLogo />
                        <div className={`${styles.headerRightText} notranslate`}>
                            News | Announcements | Events
                        </div>
                    </button>
                    <div className={styles.contentContainer} style={{
                        height: contentHeight,
                    }}>
                        <Scrollbar trackYProps={{
                            style: {width: 6},
                        }}>
                            <div className={styles.scroller}>
                                {userStore.isEditor && <div className={styles.topButtons}>
                                    <button onClick={() => SafeRouter.setQuery("spview", "SchoolFeedPost")}>
                                        <FontAwesome prefix={'fas'} name={'fa-plus'} />
                                        Add New Post
                                    </button>
                                    <button onClick={() => SafeRouter.setQuery("spview", "Social")}>
                                        <FontAwesome prefix={'fas'} name={'fa-cog'} />
                                        Sources & Settings
                                    </button>
                                </div>}
                                <p>SchoolFeed is your center for news and events.</p>
                                <ErrorBoundary>
                                    <SchoolFeedList showMedia={true} allowCuration={true}
                                                    content={schoolFeedStore.content}/>
                                    {schoolFeedStore.isMore && <div className={styles.fetchMoreContainer}>
                                        <Button disabled={schoolFeedStore.isFetching}
                                                onClick={fetchMore}>{schoolFeedStore.isFetching ?
                                                    <FontAwesome name={'fas fa-spinner'} spin/> : "View More"}</Button></div>}
                                </ErrorBoundary>
                            </div>
                        </Scrollbar>
                    </div>

                </div>
            </div>
        </SchoolFeedStoreContext.Provider>
    </ErrorBoundary>
});

export default SchoolFeedWidget;
